import React from 'react';

import type { ColumnDef } from '@tanstack/react-table';

import { ChainSequenceCell } from './ChainSequenceCell';
import { LiabilityBadgeCell } from './LiabilityBadgeCell';
import { NodeCell } from './NodeCell';
import { SequenceSelectCell, SequenceSelectHeader } from './SequenceSelectColumn';
import type { RegionNumbering } from '../../../generated/generatedOpenAPI';
import type { SequenceData } from '../../types';
import type { SequenceRow } from '../../types/antibodyTable';
import { highlightText } from '../../utilities/highlightText';
import { LiabilityColumnHeader } from '../common/LiabilityColumnHeader';

const s = {
  cellText: 'text-xs block',
} as const;

/**
 * Single source of truth for the inner sequence table's fixed (non-chain) column pixel sizes.
 * Consumed by the column defs below (each `size`) AND by getInnerTableWidth(), so the outer
 * table can size itself to contain this nested table without re-typing the numbers. Add any new
 * fixed column here rather than as an inline `size`.
 */
export const INNER_COLUMN_SIZES = {
  select: 30,
  node: 56,
  nodeSize: 92,
  aps: 84,
  liabilities: 104,
  shms: 104,
} as const;

const INNER_FIXED_COLS_WIDTH = Object.values(INNER_COLUMN_SIZES).reduce((sum, w) => sum + w, 0);

/**
 * Total rendered width of an InnerChildSequenceTable: the fixed columns plus whichever chain
 * columns are visible for the given chainType.New columns register their size in INNER_COLUMN_SIZES.
 */
export function getInnerTableWidth(heavyChainWidth: number, lightChainWidth: number, chainType: 'all' | 'heavy' | 'light'): number {
  return INNER_FIXED_COLS_WIDTH + (chainType !== 'light' ? heavyChainWidth : 0) + (chainType !== 'heavy' ? lightChainWidth : 0);
}

export interface InnerChildSequenceTableColumnsParams {
  clonotypeId: number;
  sequences: SequenceData[];
  isSequenceSelected: (clonotypeId: number, sequenceId: string) => boolean;
  onSequenceToggle: (clonotypeId: number, sequenceId: string) => void;
  onRegionClick: (region: string | null) => void;
  chainType: 'all' | 'heavy' | 'light';
  searchQuery: string;
  showFWR: boolean;
  highlightedRegions: string[];
  showLiabilities: boolean;
  showShms: boolean;
  heavyChainWidth: number;
  lightChainWidth: number;
  heavyRegions?: RegionNumbering[];
  lightRegions?: RegionNumbering[] | null;
}

/**
 * Function for InnerChildSequenceTable column definitions.
 * Returns a plain array
 */
export function InnerChildSequenceTableColumns({
  clonotypeId,
  sequences,
  isSequenceSelected,
  onSequenceToggle,
  onRegionClick,
  chainType,
  searchQuery,
  showFWR,
  highlightedRegions,
  showLiabilities,
  showShms,
  heavyChainWidth,
  lightChainWidth,
  heavyRegions,
  lightRegions,
}: InnerChildSequenceTableColumnsParams): ColumnDef<SequenceRow>[] {
  return [
    {
      id: 'select',
      header: () => (
        <SequenceSelectHeader clonotypeId={clonotypeId} sequences={sequences} isSequenceSelected={isSequenceSelected} onSequenceToggle={onSequenceToggle} />
      ),
      cell: ({ row }) => (
        <SequenceSelectCell
          clonotypeId={row.original.clonotypeId}
          sequenceId={row.original.sequenceId}
          isSequenceSelected={isSequenceSelected}
          onSequenceToggle={onSequenceToggle}
        />
      ),
      size: INNER_COLUMN_SIZES.select,
      enableSorting: false,
    },
    {
      id: 'node',
      header: 'Node',
      accessorKey: 'nodeLabel',
      cell: ({ row }) => <NodeCell nodeLabel={row.original.nodeLabel} barcodes={row.original.sequenceData.metadata.barcodes} searchQuery={searchQuery} />,
      size: INNER_COLUMN_SIZES.node,
    },
    {
      id: 'nodeSize',
      header: 'Node size',
      accessorKey: 'nodeSize',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.nodeSize.toString(), searchQuery)}</span>,
      size: INNER_COLUMN_SIZES.nodeSize,
    },
    {
      id: 'aps',
      header: 'APS',
      accessorKey: 'aps',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.aps.toFixed(2), searchQuery)}</span>,
      size: INNER_COLUMN_SIZES.aps,
    },
    {
      id: 'liabilities',
      header: () => <LiabilityColumnHeader />,
      accessorKey: 'liabilityScore',
      cell: ({ row }) => <LiabilityBadgeCell sequenceData={row.original.sequenceData} searchQuery={searchQuery} />,
      size: INNER_COLUMN_SIZES.liabilities,
    },
    {
      id: 'shms',
      header: 'SHMs',
      accessorKey: 'shms',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.shms.toString(), searchQuery)}</span>,
      size: INNER_COLUMN_SIZES.shms,
    },
    ...(chainType !== 'light'
      ? [
          {
            id: 'heavyChain',
            header: 'Heavy Chains (IGH)',
            accessorFn: (row: SequenceRow) => row.sequenceData.heavyChain?.sequence,
            cell: ({ row }: { row: { original: SequenceRow } }) => (
              <ChainSequenceCell
                sequence={row.original.sequenceData}
                chainKey="heavy"
                showFWR={showFWR}
                highlightedRegions={highlightedRegions}
                searchQuery={searchQuery}
                showLiabilities={showLiabilities}
                showShms={showShms}
                onRegionClick={onRegionClick}
                regionNumbers={heavyRegions}
              />
            ),
            size: heavyChainWidth,
          } as ColumnDef<SequenceRow>,
        ]
      : []),
    ...(chainType !== 'heavy'
      ? [
          {
            id: 'lightChain',
            header: 'Light Chains (IGL)',
            accessorFn: (row: SequenceRow) => row.sequenceData.lightChain?.sequence,
            cell: ({ row }: { row: { original: SequenceRow } }) => (
              <ChainSequenceCell
                sequence={row.original.sequenceData}
                chainKey="light"
                showFWR={showFWR}
                highlightedRegions={highlightedRegions}
                searchQuery={searchQuery}
                showLiabilities={showLiabilities}
                showShms={showShms}
                onRegionClick={onRegionClick}
                regionNumbers={lightRegions ?? undefined}
              />
            ),
            size: lightChainWidth,
          } as ColumnDef<SequenceRow>,
        ]
      : []),
  ];
}
